import { Stack } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useParams } from "react-router";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { ReportType } from "../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext";
import useDefineViewReportData from "../../../hooks/useDefineViewReportData";
import { selectCurrentReport, selectCurrentReportType } from "../../../store/selectors/currentReportSelectors";
import type { AppDispatch } from "../../../store/store";
import { createMetaDataAction } from "../../../store/utils/storeUtils";
import ApplyingDialog from "../../common/dialogs/ApplyingDialog";
import DrillDownEntry from "../common/drillDown/DrillDownEntry";
import ViewCommonDataProvider from "../common/ViewCommonDataProvider";
import ReportNotFoundOrAccessForbidden from "../ReportNotFoundOrAccessForbidden";
import ViewReportPage from "./ViewReportPage";

const ViewRoot = () => {
  const { id } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const report = useSelector(selectCurrentReport);
  const reportType = useSelector(selectCurrentReportType);
  const { getReportsUrl } = useEnhancedBiApiClientProvider();
  const { showReportNotFound, isSwitchingReport, isFetching, runState, dispatchRunState } = useDefineViewReportData();

  useEffect(() => {
    if (reportType !== undefined) {
      dispatch(createMetaDataAction("updateReportType", reportType));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [reportType]);

  if (id === undefined) {
    return <Navigate to={""} />;
  }

  if (showReportNotFound === true || (report && reportType !== ReportType.Tabular)) {
    return <ReportNotFoundOrAccessForbidden getReportsUrl={getReportsUrl} />;
  }

  const isFetchingViewReport = !isSwitchingReport && isFetching;

  if (report === undefined || isFetchingViewReport) {
    return <InlineLoader text="Loading report..." />;
  }

  return (
    <ViewCommonDataProvider dispatchRunState={dispatchRunState} runState={runState}>
      <Stack sx={{ bgcolor: "background.paper", height: "100%", width: "100%" }}>
        {isSwitchingReport && <ApplyingDialog open text="Loading report..." />}
        <ViewReportPage runState={runState} />
        <DrillDownEntry reportId={id} />
      </Stack>
    </ViewCommonDataProvider>
  );
};

export default ViewRoot;
